/**
 * useGettingStartedListener — global, app-wide listener for the Getting
 * Started panel. Mounted once from App.tsx (mirrors the useAmbientSuggestions /
 * activityFeedStore "singleton listener" pattern).
 *
 * Listens for the same events other global systems already emit on entity
 * creation, integration connection, and plan upgrades, and claims the
 * matching credit reward via POST /api/getting-started/claim. The backend
 * re-verifies every claim before granting, so this hook only needs to decide
 * *when* to ask — never whether the reward is valid.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './useAuth';
import { entityEventService, EntityEvent } from '../services/entityEventService';
import { useGettingStartedStore, GettingStartedTaskType } from '../stores/gettingStartedStore';
import fetchService from '../services/fetchService';
import { API_BASE_URL } from '../api/config';
import { analytics } from '../services/analytics/analytics';
import { createFileLogger } from '../utils/logger';

const log = createFileLogger('GETTING_STARTED_LISTENER');

async function claimGettingStartedTask(taskType: GettingStartedTaskType, entityId?: string): Promise<void> {
  const store = useGettingStartedStore.getState();
  if (store.tasks[taskType]?.completed) return; // already claimed — skip the round trip

  try {
    const result: { granted: boolean; amount_usd: number } = await fetchService(
      `${API_BASE_URL}/getting-started/claim`,
      {
        method: 'POST',
        body: JSON.stringify({ task_type: taskType, entity_id: entityId }),
      },
    );

    if (result.granted) {
      store.markCompleted(taskType, result.amount_usd);
      analytics.track('getting_started_task_claimed', { task_type: taskType, amount_usd: result.amount_usd });
      log.log('[GettingStartedListener] claimed', taskType, result.amount_usd);
    } else {
      // granted: false without a thrown error only happens when grant_reward()
      // finds the task already claimed server-side (idempotent no-op) — an
      // unverified claim raises HTTP 400, which the catch block below handles
      // instead. Reconcile local state silently for the already-claimed case.
      store.markCompleted(taskType, result.amount_usd || store.tasks[taskType]?.amountUsd || 0);
    }
  } catch (err) {
    analytics.track('getting_started_task_claim_failed', { task_type: taskType, error: String(err) });
    log.warn('[GettingStartedListener] claim failed', taskType, err);
  }
}

export function useGettingStartedListener(): void {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  // Load initial completion state once authenticated.
  useEffect(() => {
    if (!isAuthenticated) return;
    useGettingStartedStore.getState().fetchStatus();
  }, [isAuthenticated]);

  // Entity creation — first document / first folder.
  useEffect(() => {
    if (!isAuthenticated) return;

    const unsubscribe = entityEventService.onEntityEvent('created', (event: EntityEvent) => {
      const entityType = event.entity?.entityType;
      const entityId = String((event.entity as any)?._id || '');
      if (!entityId) return;

      if (entityType === 'Doc') {
        claimGettingStartedTask('create_first_doc', entityId);
      } else if (entityType === 'Group') {
        claimGettingStartedTask('create_first_folder', entityId);
      }
    });

    return unsubscribe;
  }, [isAuthenticated]);

  // Integration connected — native/world-event path + Composio path.
  useEffect(() => {
    if (!isAuthenticated) return;

    const handleWorldEvent = (e: Event) => {
      const detail = (e as CustomEvent<{ type?: string }>).detail;
      if (detail?.type === 'integration.connected') {
        claimGettingStartedTask('connect_first_integration');
      }
    };
    const handleComposioConnected = () => {
      claimGettingStartedTask('connect_first_integration');
    };

    window.addEventListener('river:worldevent', handleWorldEvent);
    window.addEventListener('composio-integration-connected', handleComposioConnected);
    return () => {
      window.removeEventListener('river:worldevent', handleWorldEvent);
      window.removeEventListener('composio-integration-connected', handleComposioConnected);
    };
  }, [isAuthenticated]);

  // Plan upgrade — embedded-checkout paywall path.
  useEffect(() => {
    if (!isAuthenticated) return;

    const handlePlanUpgraded = () => {
      claimGettingStartedTask('upgrade_plan');
    };

    window.addEventListener('plan-upgraded', handlePlanUpgraded);
    return () => window.removeEventListener('plan-upgraded', handlePlanUpgraded);
  }, [isAuthenticated]);

  // Plan upgrade backstop — hosted-redirect / org-gate checkout path does not
  // fire `plan-upgraded`, so re-check once (and again after a short delay, in
  // case the Stripe webhook hasn't landed yet) whenever this route is hit.
  useEffect(() => {
    if (!isAuthenticated) return;
    if (location.pathname !== '/org-payment-success') return;

    claimGettingStartedTask('upgrade_plan');
    const timeout = window.setTimeout(() => claimGettingStartedTask('upgrade_plan'), 3000);
    return () => window.clearTimeout(timeout);
  }, [isAuthenticated, location.pathname]);
}
